<script setup>
import { ref, computed } from "vue";
import RegionCombo from "../components/RegionCombo.vue";
import { getApiBaseUrl } from "../components/ApiBaseUrl.js";
import * as XLSX from "xlsx";

const region = ref("000"); // 선택하세요. 서울시 전체

const now = new Date();
// 3개월 이전
const d = new Date(now.getFullYear(), now.getMonth() - 3, 1);
const fromYM = ref(
  String(d.getFullYear()) + String(d.getMonth() + 1).padStart(2, "0"),
);
const toYM = ref(now.toISOString().slice(0, 7).replace("-", "")); // 예 "202601"

// 전용평수(밴드)
const areas = ref("30s");

// 가격(억)
const priceMin = ref("10.0");
const priceMax = ref("15.0");

const loading = ref(false);
const error = ref("");
const top30 = ref([]);

// 정렬 상태
const sortKey = ref(""); // 'rank','apt','district','dong','totalCount','averagePrice'
const sortDir = ref(1); // 1 = asc, -1 = desc

function setSort(key) {
  if (sortKey.value === key) {
    sortDir.value = -sortDir.value;
  } else {
    sortKey.value = key;
    sortDir.value = 1;
  }
}

function getValue(item, key) {
  const v = item[key];
  const numericKeys = ["rank", "averagePrice", "totalCount"];
  if (numericKeys.includes(key)) {
    const n = parseFloat(String(v).replace(/[^0-9.-]/g, ""));
    return Number.isFinite(n) ? n : Number.NEGATIVE_INFINITY;
  }
  return String(v ?? "").toLowerCase();
}

const sortedDeals = computed(() => {
  const arr = (top30.value || []).slice();
  if (!sortKey.value) return arr;
  arr.sort((a, b) => {
    const va = getValue(a, sortKey.value);
    const vb = getValue(b, sortKey.value);
    if (va < vb) return -1 * sortDir.value;
    if (va > vb) return 1 * sortDir.value;
    return 0;
  });
  return arr;
});

const API_BASE = getApiBaseUrl();

function computeDateRangeFromYYYYMM(yyyymmStr) {
  const s = String(yyyymmStr || "").trim();
  if (/^\d{4}$/.test(s)) {
    const year = Number(s);
    const start = `${s}-01-01`;
    const end = `${year + 1}-01-01`;
    return { start, end };
  }
  const year = Number(s.slice(0, 4)) || new Date().getFullYear();
  const month = Number(s.slice(4, 6)) || 1;
  const start = `${String(year).padStart(4, "0")}-${String(month).padStart(2, "0")}-01`;
  const next = new Date(year, month, 1); // 다음 달 1일 (Date의 month는 0기반이라 month를 그대로 쓰면 +1달)
  const endYear = next.getFullYear();
  const endMonth = String(next.getMonth() + 1).padStart(2, "0");
  const end = `${endYear}-${endMonth}-01`;
  return { start, end };
}

/**
 * 평형대별 기본 전용률 범위(추정)
 */
const DEFAULT_RATIO_BY_BAND = {
  lt10: { min: 0.82, max: 0.86 },
  "10s": { min: 0.8, max: 0.84 },
  "20s": { min: 0.78, max: 0.82 },
  "30s": { min: 0.76, max: 0.8 },
  "40s": { min: 0.75, max: 0.79 },
  "50s": { min: 0.74, max: 0.78 },
  gt50: { min: 0.73, max: 0.77 },
};

/**
 * 선택된 평형대(공급평형 밴드)를 전용평형 범위로 추정
 * 반환: { typical: [minExclusive, maxExclusive] } (여기선 min/max 전용률로 투영)
 */
function estimateExclusiveForPyeong(opts = {}) {
  const band = areas.value;
  if (!band) return null;

  const ratios = DEFAULT_RATIO_BY_BAND;
  const r = ratios[band] || ratios["30s"];
  const digits = Number.isInteger(opts.digits) ? opts.digits : 2;
  const pow = Math.pow(10, digits);

  let sMin = 0;
  let sMax = 0;

  if (areas.value === "all") {
    sMin = 0;
    sMax = 1000;
  } else if (areas.value === "lt10") {
    sMin = 0;
    sMax = 10;
  } else if (areas.value === "gt50") {
    sMin = 60;
    sMax = 1000;
  } else {
    sMin = Number(areas.value.slice(0, -1)); // "10s" -> 10
    sMax = sMin + 10; // 10평 단위
  }

  const exMin = Math.round(sMin * r.min * pow) / pow;
  const exMax = Math.round((sMax - Number.EPSILON) * r.max * pow) / pow;

  return { typical: [exMin, exMax] };
}

async function fetchDeals() {
  loading.value = true;
  error.value = "";
  top30.value = [];

  if (fromYM.value && toYM.value && fromYM.value > toYM.value) {
    error.value = "기준월 범위가 올바르지 않습니다.";
    loading.value = false;
    return;
  }

  try {
    const body = {
      district: region.value || "",
      minPrice: priceMin.value || "10",
      maxPrice: priceMax.value || "15",
      startDate: fromYM.value
        ? computeDateRangeFromYYYYMM(fromYM.value).start
        : start,
      endDate: toYM.value ? computeDateRangeFromYYYYMM(toYM.value).end : end,
      minArea: estimateExclusiveForPyeong()?.typical[0],
      maxArea: estimateExclusiveForPyeong()?.typical[1],
    };

    const res = await fetch(`${API_BASE}/api/apartment-top30`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });
    if (!res.ok) throw new Error("network");
    const data = await res.json();
    if (!data.ok) {
      error.value = data.message || "서버 오류";
      return;
    }

    // ✅ 서버가 { ok: true, rows: [...] } 를 반환한다고 가정
    //   만약 서버가 top30Rows 라는 키로 반환한다면 아래 한 줄을 data.top30Rows로 바꿔주세요.
    top30.value = (data.top30Rows || []).map((r, idx) => ({
      id: idx + 1,
      rank: Number(r.순위) || 0,
      apt: r.단지명 || "",
      district: r.구군 || "",
      dong: r.동읍 || "",
      totalCount: Number(r.거래건수) || 0,
      averagePrice: Number(r.평균가) || 0,
    }));
  } catch (e) {
    console.error("fetchDeals error", e);
    error.value = "조회 중 오류가 발생했습니다.";
  } finally {
    loading.value = false;
  }
}

/**
 * 요약(Top30) + 상세(거래내역) 까지 전체 엑셀로 내보내기
 * - Sheet1: "Top30"
 * - Sheet2: "상세내역" (모든 행의 상세를 한 시트로 합침)
 *
 * 필요 시: 행별로 시트를 쪼개고 싶다면 아래의 주석된 "행별 시트" 부분 참고
 */
async function exportExcelWithDetails() {
  try {
    // 0) 기본 체크
    if (!top30.value || !top30.value.length) {
      alert("조회 결과가 없습니다. 먼저 조회하세요.");
      return;
    }

    // 1) 상세 데이터가 없는 행은 자동으로 조회(fetch) → 캐시에 채우기
    //    (정렬된 순서대로 보장)
    for (const d of sortedDeals.value) {
      const cached = detailById.value[d.id];
      if (!cached || !Array.isArray(cached) || !cached.length) {
        // 펼치지 않았던 행도 엑셀에 포함하려면 여기서 강제 조회
        await fetchRowDetail(d);
      }
    }

    // 2) 워크북 생성
    const wb = XLSX.utils.book_new();

    // 3) Sheet1: Top30 요약
    {
      const headers = [
        "순위",
        "단지명",
        "구군",
        "동읍",
        "거래건수",
        "평균가(억)",
      ];
      const fields = [
        "rank",
        "apt",
        "district",
        "dong",
        "totalCount",
        "averagePrice",
      ];
      const aoa = [headers].concat(
        sortedDeals.value.map((d) => fields.map((f) => d[f] ?? "")),
      );
      const ws = XLSX.utils.aoa_to_sheet(aoa);

      // (선택) 열 너비
      ws["!cols"] = [
        { wch: 6 }, // 순위
        { wch: 28 }, // 단지명
        { wch: 10 }, // 구군
        { wch: 10 }, // 동읍
        { wch: 10 }, // 거래건수
        { wch: 12 }, // 평균가(억)
      ];

      XLSX.utils.book_append_sheet(wb, ws, "Top30");
    }

    // 4) Sheet2: 상세 내역(모든 행을 한 시트에 합치기)
    {
      const detailHeaders = [
        "순위",
        "단지명",
        "구군",
        "동읍",
        "계약일자",
        "전용면적(평)",
        "매매가(억)",
        "동",
        "층",
        "취소일",
      ];

      // 행별 상세를 펼쳐서 하나의 AOA로 합치기
      const detailAOA = [detailHeaders];

      for (const d of sortedDeals.value) {
        const rows = detailById.value[d.id] || [];
        if (!rows.length) {
          // 상세 없음도 명시하고 싶다면 주석 해제
          // detailAOA.push([d.rank, d.apt, d.district, d.dong, "-", "-", "-", "-", "-", "-"]);
          continue;
        }
        for (const r of rows) {
          detailAOA.push([
            d.rank,
            d.apt,
            d.district,
            d.dong,
            r.contractDate || "",
            Number.isFinite(r.area) ? r.area : "",
            Number.isFinite(r.txPrice) ? r.txPrice : "",
            r.dong || "",
            r.floor ?? "",
            r.cancelDate || "",
          ]);
        }
      }

      const wsDetail = XLSX.utils.aoa_to_sheet(detailAOA);

      // (선택) 열 너비 지정
      wsDetail["!cols"] = [
        { wch: 6 }, // 순위
        { wch: 28 }, // 단지명
        { wch: 10 }, // 구군
        { wch: 10 }, // 동읍
        { wch: 12 }, // 계약일자
        { wch: 12 }, // 전용면적(평)
        { wch: 12 }, // 매매가(억)
        { wch: 8 }, // 동
        { wch: 6 }, // 층
        { wch: 12 }, // 취소일
      ];

      XLSX.utils.book_append_sheet(wb, wsDetail, "상세내역");
    }

    // 5) 파일 저장
    XLSX.writeFile(wb, "부동산실거래_Top30_상세포함.xlsx");
  } catch (e) {
    console.error("exportExcelWithDetails error", e);
    alert("엑셀 내보내기 중 오류가 발생했습니다.");
  }
}

/* --- 상세(행 아래 표시) 상태/로직 --- */
const expanded = ref({}); // { [id]: boolean }
const detailLoading = ref({}); // { [id]: boolean }
const detailError = ref({}); // { [id]: string }
const detailById = ref({}); // { [id]: Array<{...}> }

function makeDetailCacheKey(d) {
  return JSON.stringify({
    apt: d.apt,
    district: d.district,
    dong: d.dong || "",
    from: computeDateRangeFromYYYYMM(fromYM.value).start,
    to: computeDateRangeFromYYYYMM(toYM.value).end,
    minArea: estimateExclusiveForPyeong()?.typical[0],
    maxArea: estimateExclusiveForPyeong()?.typical[1],
    minPrice: priceMin.value,
    maxPrice: priceMax.value,
  });
}

async function fetchRowDetail(d) {
  const id = d.id;
  const cacheKey = makeDetailCacheKey(d);
  if (detailById.value[id]?.__cacheKey === cacheKey) return;

  detailLoading.value[id] = true;
  detailError.value[id] = "";

  try {
    const start = computeDateRangeFromYYYYMM(fromYM.value).start;
    const end = computeDateRangeFromYYYYMM(toYM.value).end;
    const body = {
      district: d.district,
      apartment: d.apt,
      dong: d.dong || null,
      startDate: start,
      endDate: end,
      minArea: estimateExclusiveForPyeong()?.typical[0],
      maxArea: estimateExclusiveForPyeong()?.typical[1],
      minPrice: priceMin.value || null,
      maxPrice: priceMax.value || null,
    };

    const res = await fetch(`${API_BASE}/api/apartment-top30-detail`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });
    if (!res.ok) throw new Error("network");
    const data = await res.json();
    if (!data.ok) throw new Error(data.message || "server error");

    const rows = (data.rows || []).map((r) => ({
      contractDate: r.계약일자, // YYYY-MM-DD
      area: Number(r.전용면적), // 평
      txPrice: Number(r.매매가), // 억
      dong: r.동,
      floor: r.층,
      cancelDate: r.취소일 || "",
    }));

    detailById.value[id] = Object.assign([], rows, { __cacheKey: cacheKey });
  } catch (e) {
    console.error("fetchRowDetail error", e);
    detailError.value[id] = "세부 데이터 조회 중 오류";
    detailById.value[id] = [];
  } finally {
    detailLoading.value[id] = false;
  }
}

function toggleRow(d) {
  const id = d.id;
  expanded.value[id] = !expanded.value[id];
  if (expanded.value[id]) {
    fetchRowDetail(d);
  }
}
</script>

<template>
  <div class="wrap">
    <div class="panel">
      <h2>Top30 조회</h2>

      <div class="filters">
        <label>
          지역
          <RegionCombo v-model="region" />
        </label>

        <label>
          전용평수
          <div style="display: flex; gap: 4px; width: 100%">
            <select
              id="areas"
              v-model="areas"
              class="combo-select"
              style="flex: 1; width: 20%"
            >
              <option value="lt10">~ 10평</option>
              <option value="10s">10평대</option>
              <option value="20s">20평대</option>
              <option value="30s">30평대</option>
              <option value="40s">40평대</option>
              <option value="50s">50평대</option>
              <option value="gt50">50평~</option>
              <option value="all">전체</option>
            </select>
          </div>
        </label>

        <label>
          실거래 평균가(억)
          <div>
            <input
              id="priceMin"
              v-model="priceMin"
              placeholder="예: 10.0"
              style="flex: 1; width: 40%"
            />
            <input
              id="priceMax"
              v-model="priceMax"
              placeholder="예: 15.0"
              style="flex: 1; width: 40%"
            />
          </div>
        </label>

        <label>
          기준월(YYYYMM)
          <div>
            <input
              id="fromYM"
              v-model="fromYM"
              placeholder="예: 202502"
              style="flex: 1; width: 50%"
            />
            <input
              id="toYM"
              v-model="toYM"
              placeholder="예: 202602"
              style="flex: 1; width: 50%"
            />
          </div>
        </label>

        <div style="display: flex; gap: 8px; align-items: center">
          <button
            class="btn"
            @click="fetchDeals"
            :disabled="loading"
            style="width: 50%"
          >
            {{ loading ? "조회중..." : "조회" }}
          </button>
          <button
            class="btn"
            @click="exportExcelWithDetails"
            :disabled="loading"
            style="width: 50%"
          >
            Excel
          </button>
        </div>
      </div>

      <p v-if="error" class="error">{{ error }}</p>

      <div class="list">
        <!-- 헤더 -->
        <div class="row head">
          <span class="clickable" @click="setSort('rank')">
            순위<span v-if="sortKey === 'rank'">{{
              sortDir === 1 ? " ▲" : " ▼"
            }}</span>
          </span>
          <span class="clickable" @click="setSort('apt')">
            단지명<span v-if="sortKey === 'apt'">{{
              sortDir === 1 ? " ▲" : " ▼"
            }}</span>
          </span>
          <span class="clickable" @click="setSort('district')">
            구군<span v-if="sortKey === 'district'">{{
              sortDir === 1 ? " ▲" : " ▼"
            }}</span>
          </span>
          <span class="clickable" @click="setSort('dong')">
            동읍<span v-if="sortKey === 'dong'">{{
              sortDir === 1 ? " ▲" : " ▼"
            }}</span>
          </span>
          <span class="clickable" @click="setSort('totalCount')">
            거래건수<span v-if="sortKey === 'totalCount'">{{
              sortDir === 1 ? " ▲" : " ▼"
            }}</span>
          </span>
          <span class="clickable" @click="setSort('averagePrice')">
            평균가(억)<span v-if="sortKey === 'averagePrice'">{{
              sortDir === 1 ? " ▲" : " ▼"
            }}</span>
          </span>
        </div>

        <!-- 본문: 각 행 + (바로 아래) 상세 -->
        <template v-if="sortedDeals.length">
          <div class="group" v-for="d in sortedDeals" :key="d.id">
            <!-- 클릭 가능한 행 -->
            <div
              class="row item"
              @click="toggleRow(d)"
              :aria-expanded="!!expanded[d.id]"
              role="button"
              tabindex="0"
            >
              <span>{{ d.rank }}</span>
              <span>{{ d.apt }}</span>
              <span>{{ d.district }}</span>
              <span>{{ d.dong }}</span>
              <span style="text-align: right">{{ d.totalCount }}</span>
              <span style="text-align: right" class="price">{{
                d.averagePrice
              }}</span>
            </div>

            <!-- 상세: 해당 행과 다음 행 사이 -->
            <transition name="expand">
              <div v-if="expanded[d.id]" class="detail">
                <div class="detail-inner">
                  <div v-if="detailLoading[d.id]" class="detail-loading">
                    불러오는 중…
                  </div>
                  <div v-else-if="detailError[d.id]" class="detail-error">
                    {{ detailError[d.id] }}
                  </div>

                  <template v-else>
                    <div
                      v-if="(detailById[d.id] || []).length"
                      class="detail-table"
                    >
                      <div class="detail-header">
                        <span>계약일자</span>
                        <span>전용면적</span>
                        <span style="text-align: right">매매가</span>
                        <span style="text-align: center">동</span>
                        <span>층</span>
                        <span>취소일</span>
                      </div>
                      <div
                        v-for="r in detailById[d.id]"
                        :key="`${d.id}-${r.contractDate}-${r.area}-${r.floor}`"
                        class="detail-row"
                      >
                        <span>{{ r.contractDate }}</span>
                        <span>{{ r.area }}평</span>
                        <span class="num">{{ r.txPrice.toFixed(2) }}</span>
                        <span>{{ r.dong }}</span>
                        <span>{{ r.floor }}</span>
                        <span>{{ r.cancelDate }}</span>
                      </div>
                    </div>
                    <div v-else class="detail-empty">
                      해당 기간 거래 내역이 없습니다.
                    </div>
                  </template>
                </div>
              </div>
            </transition>
          </div>
        </template>

        <p v-else class="empty">조회 결과가 없습니다.</p>
      </div>
    </div>
  </div>
</template>

<style scoped>
.wrap {
  max-width: 99.5%;
}
.panel {
  padding: 18px;
  border-radius: 14px;
  background: rgba(255, 255, 255, 0.06);
  border: 1px solid rgba(255, 255, 255, 0.1);
}
.filters {
  display: grid;
  grid-template-columns: 0.5fr repeat(4, 1fr);
  gap: 10px;
  align-items: end;
  margin-top: 12px;
}
label {
  display: flex;
  flex-direction: column;
  gap: 2px;
  font-size: 13px;
  opacity: 0.95;
}
input,
select {
  padding: 10px 12px;
  border-radius: 12px;
  border: 1px solid rgba(255, 255, 255, 0.12);
  background: rgba(0, 0, 0, 0.18);
  color: #eaf2ff;
}
.btn {
  height: 40px;
  border-radius: 12px;
  border: 0;
  background: #2d8cff;
  color: #fff;
  font-weight: 800;
  cursor: pointer;
}
.btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}
.combo-select {
  padding: 8px 10px;
  border-radius: 10px;
  border: 1px solid rgba(255, 255, 255, 0.12);
  background: rgba(0, 0, 0, 0.16);
  color: #ffffff;
}
.combo-select option {
  color: #111827;
  background: #ffffff;
}
.error {
  color: #ffb3b3;
  margin-top: 10px;
}
.list {
  margin-top: 14px;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  max-height: 580px; /* 내부 스크롤 영역 */
  overflow-y: auto;
}
.row {
  display: grid;
  grid-template-columns: 0.3fr 2fr 0.8fr 0.8fr 0.5fr 0.5fr; /* 순위, 단지명, 구군, 동읍, 건수, 평균가 */
  gap: 10px;
  padding: 10px 2px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
}
.head {
  opacity: 0.8;
  font-weight: 700;
}
.clickable {
  cursor: pointer;
  user-select: none;
}
.price {
  font-weight: 900;
  color: #bfe0ff;
  text-align: center;
}
.empty {
  margin-top: 14px;
  opacity: 0.8;
}

/* 목록 행 */
.row.item {
  cursor: pointer;
}
.row.item:hover {
  background: rgba(255, 255, 255, 0.04);
}

/* 상세 박스 (행과 다음 행 사이) */
.detail {
  padding: 0;
}
.detail-inner {
  margin: 0 2px 10px 2px;
  padding: 12px;
  border-radius: 10px;
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(255, 255, 255, 0.1);
}

/* 상세 표 레이아웃 */
.detail-table {
  display: block;
}
.detail-header,
.detail-row {
  display: grid;
  grid-template-columns: 1.5fr 1fr 0.5fr 0.8fr 1fr 1fr; /* 계약일자, 전용면적, 매매가, 동, 층, 취소일 */
  gap: 8px;
  padding: 8px 6px;
}
.detail-header {
  font-weight: 700;
  opacity: 0.85;
  border-bottom: 1px solid rgba(255, 255, 255, 0.12);
}
.detail-row {
  border-bottom: 1px dashed rgba(255, 255, 255, 0.06);
}
.detail-row:last-child {
  border-bottom: 0;
}
.detail-row .num {
  text-align: right;
  font-variant-numeric: tabular-nums;
}

.detail-loading {
  opacity: 0.8;
}
.detail-error {
  color: #ffb3b3;
}
.detail-empty {
  opacity: 0.8;
}

/* 펼침 애니메이션 */
.expand-enter-active,
.expand-leave-active {
  transition:
    max-height 200ms ease,
    opacity 180ms ease;
  overflow: hidden;
}
.expand-enter-from,
.expand-leave-to {
  max-height: 0;
  opacity: 0;
}
.expand-enter-to,
.expand-leave-from {
  max-height: 480px;
  opacity: 1;
}

/* 그룹 컨테이너를 grid로 만들어 헤더와 동일한 컬럼 정의 적용 */
.group {
  display: grid;
  grid-template-columns: 0.3fr 2fr 0.8fr 0.8fr 0.5fr 0.5fr; /* 헤더 .row 와 동일 */
  gap: var(--list-gap, 10px);
}

/* 목록 행은 전체 열을 차지 */
.group > .row.item {
  grid-column: 1 / -1;
}

/* 상세는 2열(단지명 컬럼)부터 끝까지 차지 → 단지명 왼쪽에 정렬 */
.group > .detail {
  grid-column: 2 / -1;
  /* grid gap 만큼 왼쪽 마진을 추가하여 시각적 틈새 보정 (선택) */
  margin-left: calc(var(--list-gap, 10px));
}

/* 기존 상세 바디 스타일 유지 가능 */
.detail-inner {
  margin: 0 0 10px 0; /* 좌측 정렬은 grid로 해결했으니 좌/우 마진은 필요시만 */
  padding: 12px;
  border-radius: 10px;
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(255, 255, 255, 0.1);
}

/* 헤더/본문의 .row 그리드 정의와 시각 일관성(기존 유지) */
.row {
  display: grid;
  grid-template-columns: 0.3fr 2fr 0.8fr 0.8fr 0.5fr 0.5fr;
  gap: var(--list-gap, 10px);
  padding: 10px 2px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
}

/* 모바일 대응: 일부 컬럼을 숨기고 컬럼 수가 바뀌므로 상세는 1열부터로 변경 */
@media (max-width: 900px) {
  .filters {
    grid-template-columns: 1fr 1fr;
  }
  .group {
    /* 모바일에서 목록 .row가 4열로 바뀌니 동일하게 맞춤 */
    grid-template-columns: 2fr 1fr 1fr 0.8fr;
  }

  .group > .detail {
    grid-column: 1 / -1; /* 모바일에선 전체 폭 사용 */
    margin-left: 0; /* gap 보정 불필요 */
  }

  /* 기존 모바일에서 숨기는 컬럼 규칙 유지 */
  .row {
    grid-template-columns: 2fr 1fr 1fr 0.8fr;
  }
  .row span:nth-child(3),
  .row span:nth-child(4),
  .row span:nth-child(6) {
    display: none;
  }
}
</style>
